/**
 * MSG91 OTP Widget SDK Loader
 * Loads the MSG91 Web SDK script and provides utility functions for OTP operations
 */

let loaded = null;
let sdkInitialized = false;
let currentReqId = null;

/**
 * Load the MSG91 OTP Provider script
 * @returns {Promise<void>}
 */
export function loadMsg91() {
  if (loaded) return loaded;

  loaded = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-msg91="otp-provider"]');
    if (existing) {
      if (window.initSendOTP) {
        resolve();
      } else {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('MSG91 failed to load')), { once: true });
      }
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://verify.msg91.com/otp-provider.js';
    script.async = true;
    script.dataset.msg91 = 'otp-provider';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('MSG91 failed to load'));
    document.head.appendChild(script);
  });

  return loaded;
}

/**
 * Initialize MSG91 SDK with widget configuration
 * @returns {Promise<void>}
 */
export async function initMsg91() {
  if (sdkInitialized) return;
  
  await loadMsg91();
  
  return new Promise((resolve, reject) => {
    const widgetId = process.env.REACT_APP_MSG91_WIDGET_ID;
    const tokenAuth = process.env.REACT_APP_MSG91_WIDGET_TOKEN;
    
    if (!widgetId || !tokenAuth) {
      reject(new Error('MSG91 configuration missing'));
      return;
    }
    
    window.initSendOTP({
      widgetId,
      tokenAuth,
      exposeMethods: true,
      success: () => {
        sdkInitialized = true;
        resolve();
      },
      failure: (error) => {
        console.error('MSG91 initialization failed:', error);
        reject(new Error('MSG91 initialization failed'));
      },
    });
  });
}

/**
 * Send OTP to a phone number
 * @param {string} phone - Phone number (10 digits without country code)
 * @returns {Promise<{reqId: string, message: string}>}
 */
export async function sendOtp(phone) {
  await initMsg91();
  
  // Format phone with country code (91 for India, no + prefix)
  const identifier = phone.startsWith('91') ? phone : `91${phone}`;
  
  return new Promise((resolve, reject) => {
    window.sendOtp?.(
      identifier,
      (data) => {
        const reqId = data?.reqId ?? data?.data?.reqId;
        if (!reqId) {
          reject(new Error('MSG91 did not return a request ID'));
          return;
        }
        currentReqId = reqId;
        resolve({ reqId, message: 'OTP sent successfully' });
      },
      (error) => {
        console.error('MSG91 send OTP error:', error);
        reject(new Error(error?.message || 'Failed to send OTP'));
      }
    );
  });
}

/**
 * Resend OTP via specified channel
 * @param {string} phone - Phone number (10 digits without country code)
 * @param {string} channel - Channel: 'sms' (11), 'voice' (4), 'email' (3), 'whatsapp' (12)
 * @returns {Promise<{message: string}>}
 */
export async function resendOtp(phone, channel = 'sms') {
  await initMsg91();
  
  if (!currentReqId) {
    throw new Error('No active OTP request. Please send OTP first.');
  }
  
  // Channel codes: SMS=11, Voice=4, Email=3, WhatsApp=12
  const channelCodes = { sms: 11, voice: 4, email: 3, whatsapp: 12 };
  const channelCode = channelCodes[channel] || 11;
  
  return new Promise((resolve, reject) => {
    window.retryOtp?.(
      channelCode,
      () => {
        resolve({ message: 'OTP resent successfully' });
      },
      (error) => {
        console.error('MSG91 resend OTP error:', error);
        reject(new Error(error?.message || 'Failed to resend OTP'));
      },
      currentReqId
    );
  });
}

/**
 * Verify OTP and get access token
 * @param {string} otp - The OTP code entered by user
 * @returns {Promise<{accessToken: string}>}
 */
export async function verifyOtp(otp) {
  await initMsg91();
  
  if (!currentReqId) {
    throw new Error('No active OTP request. Please send OTP first.');
  }
  
  return new Promise((resolve, reject) => {
    window.verifyOtp?.(
      otp,
      (data) => {
        const accessToken = data?.['access-token'] ?? data?.accessToken ?? data?.token ?? data?.data?.['access-token'];
        if (!accessToken) {
          reject(new Error('MSG91 returned no access token'));
          return;
        }
        // Clear the reqId after successful verification
        currentReqId = null;
        resolve({ accessToken });
      },
      (error) => {
        console.error('MSG91 verify OTP error:', error);
        reject(new Error(error?.message || 'Invalid or expired OTP'));
      },
      currentReqId
    );
  });
}

/**
 * Clear current OTP session
 */
export function clearOtpSession() {
  currentReqId = null;
}

/**
 * Get current request ID (for debugging)
 */
export function getCurrentReqId() {
  return currentReqId;
}

export default {
  loadMsg91,
  initMsg91,
  sendOtp,
  resendOtp,
  verifyOtp,
  clearOtpSession,
  getCurrentReqId,
};
